import {
  type AfterContentInit,
  afterNextRender,
  booleanAttribute,
  Component,
  computed,
  contentChild,
  contentChildren,
  ElementRef,
  inject,
  Injector,
  input,
  type InputSignal,
  type InputSignalWithTransform,
  model,
  type ModelSignal,
  type OnDestroy,
  type OnInit,
  output,
  type OutputEmitterRef,
  signal,
  type Signal,
  viewChild,
  type WritableSignal
} from '@angular/core';
import { outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { ActiveDescendantKeyManager, FocusKeyManager } from '@angular/cdk/a11y';
import { SelectionModel } from '@angular/cdk/collections';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition
} from '@angular/cdk/overlay';
import { EMPTY, merge, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { TranslateDirective } from '@ngx-translate/core';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { ControlValueAccessor } from '@angular/forms';
import { KeyCode } from '../../domain/key-code.enum';
import { IdService } from '../../services/id.service';
import { PlatformService } from '../../services/platform.service';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IconComponent } from '../icon/icon.component';
import { DropdownItemComponent } from './dropdown-item.component';
import { DROPDOWN_SCROLL_STRATEGY, DROPDOWN_SCROLL_STRATEGY_PROVIDER } from './dropdown.domain';
import { DropdownToggleDirective } from './dropdown-toggle.directive';
import { DropdownPlaceholderDirective } from './dropdown-placeholder.directive';
import type { ConnectedOverlayPositionChange, ScrollStrategy, VerticalConnectionPos } from '@angular/cdk/overlay';
import type { SelectionChange } from '@angular/cdk/collections';
import type { ChangeEventOn, SelectableItem, SelectionChangeEvent } from '../../domain/selectable.domain';

export type DropdownType = 'regular' | 'chip';
export type DropdownToggleType = 'arrow' | 'none';
export type DropdownSize = 'regular' | 'compact';
export type DropdownBackground = 'white' | 'grey' | 'outline' | 'green';

const DEFAULT_COMPARATOR = <T>(obj1: T, obj2: T): boolean => obj1 === obj2;

@Component({
  selector: 'td-wb-dropdown',
  templateUrl: 'dropdown.component.html',
  styleUrls: ['dropdown.component.scss'],
  providers: [ValueAccessorProviderFactory(DropdownComponent), DROPDOWN_SCROLL_STRATEGY_PROVIDER],
  exportAs: 'tdWbDropdown',
  imports: [CdkOverlayOrigin, CdkConnectedOverlay, TranslateDirective, IconComponent],
  host: {
    '[attr.tabindex]': 'tabIndex()',
    '[attr.role]': '"combobox"',
    '[attr.aria-haspopup]': '"listbox"',
    '[attr.disabled]': 'isDisabled() ? "" : null',
    '[attr.aria-disabled]': 'isDisabled()',
    '[attr.aria-label]': '((ariaLabel() !== undefined) ? ariaLabel() : placeholder())',
    '[attr.aria-labelledby]': 'ariaLabelledBy()',
    '[attr.aria-activedescendant]': 'ariaActiveDescendantAttr()',
    '[attr.aria-expanded]': 'panelDisplayed()',
    '[attr.aria-controls]': 'panelDisplayed() ? \'td-wb-dropdown__panel\' : \'\'',
    '(keydown)': 'onKeyDown($event)',
    '(click)': 'onClick()'
  }
})
export class DropdownComponent<T> implements ControlValueAccessor, OnDestroy, OnInit, AfterContentInit {
  private static readonly LABEL_ID_PREFIX: string = 'td-wb-dropdown-label';

  readonly type: InputSignal<DropdownType> = input<DropdownType>('regular');
  readonly size: InputSignal<DropdownSize> = input<DropdownSize>('regular');
  readonly background: InputSignal<DropdownBackground> = input<DropdownBackground>('white');
  readonly placeholder: InputSignal<string | undefined> = input<string | undefined>();
  readonly userDefinedPanelMaxHeight: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'panelMaxHeight' });
  readonly toggleType: InputSignal<DropdownToggleType> = input<DropdownToggleType>('arrow');
  readonly comparator: InputSignal<(obj1: T, obj2: T) => boolean> = input<(obj1: T, obj2: T) => boolean>(DEFAULT_COMPARATOR);
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'ariaLabel' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly id: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'id' });

  readonly disabled: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { transform: booleanAttribute });
  private readonly disabledByCva = signal(false);
  readonly isDisabled: Signal<boolean> = computed(() => this.disabled() || this.disabledByCva());

  readonly panel: Signal<ElementRef | undefined> = viewChild<ElementRef>('panel');
  readonly connectedOverlay: Signal<CdkConnectedOverlay | undefined> = viewChild(CdkConnectedOverlay);
  readonly items: Signal<ReadonlyArray<DropdownItemComponent<T>>> = contentChildren(DropdownItemComponent, { descendants: true });
  readonly customToggle: Signal<DropdownToggleDirective | undefined> = contentChild(DropdownToggleDirective);
  readonly customPlaceHolder: Signal<DropdownPlaceholderDirective | undefined> = contentChild(DropdownPlaceholderDirective);

  readonly tabIndex: Signal<string> = computed(() => this.isDisabled() ? '-1' : '0');

  private readonly scrollStrategyFactory = inject(DROPDOWN_SCROLL_STRATEGY);
  scrollStrategy: ScrollStrategy = this.scrollStrategyFactory();
  toggleRect: WritableSignal<DOMRect | undefined> = signal<DOMRect | undefined>(undefined);
  panelPosition: WritableSignal<VerticalConnectionPos> = signal<VerticalConnectionPos>('bottom');
  preferredPositions: WritableSignal<Array<ConnectedPosition>> = signal<Array<ConnectedPosition>>([
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
  ]);

  generateLabel: WritableSignal<boolean> = signal(false);
  labelId: WritableSignal<string | undefined> = signal(undefined);
  panelMaxHeight: WritableSignal<number> = signal(0);
  platformIOS: boolean = false;
  panelDisplayed: WritableSignal<boolean> = signal(false);

  private readonly injector = inject(Injector);
  /**
   * Single source of truth, and the Signal Forms native custom-control binding.
   * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly value: ModelSignal<T | null | undefined> = model<T | null | undefined>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private readonly destroy: Subject<void> = new Subject<void>();

  private itemHeight: number = 0;
  private readonly elementRef = inject(ElementRef);
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly platformService = inject(PlatformService);
  private readonly idService = inject(IdService);
  private keyManager!: ActiveDescendantKeyManager<SelectableItem<T>> | FocusKeyManager<SelectableItem<T>>;
  private readonly selectionModel: SelectionModel<SelectableItem<T>> = new SelectionModel<SelectableItem<T>>(false);
  private readonly selectedItems: WritableSignal<ReadonlyArray<SelectableItem<T>>> = signal<ReadonlyArray<SelectableItem<T>>>([]);
  private keyManagerInitialized: boolean = false;

  private readonly items$ = toObservable(this.items, { injector: this.injector });

  private readonly optionSelectionChanges: ChangeEventOn<SelectableItem<T>> = toObservable(this.items, { injector: this.injector }).pipe(
    switchMap((items: ReadonlyArray<DropdownItemComponent<T>>) => {
      if (items.length > 0) {
        return merge(...items.map((item: DropdownItemComponent<T>) => outputToObservable(item.onSelectionChange)));
      }
      return EMPTY;
    })
  );

  constructor() {
    this.items$
      .pipe(takeUntil(this.destroy))
      .subscribe((items: ReadonlyArray<DropdownItemComponent<T>>) => {
        if (items.length === 0) {
          return;
        }

        items.forEach((item: DropdownItemComponent<T>, index: number) => {
          item.first.set(index === 0);
          item.last.set(index === items.length - 1);
        });

        this.setSelectionByValue(this.value());

        // Re-initialize key manager when items change (e.g., dynamically loaded dropdown items)
        if (this.keyManagerInitialized) {
          this.reinitializeKeyManager();
        }
      });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDisabled()) {
      if (this.panelDisplayed()) {
        this.onKeyDownOpen(event);
      } else {
        this.onKeyDownClose(event);
      }
    }
  }

  onClick(): void {
    this.toggle();
  }

  ngOnInit(): void {
    this.platformIOS = this.platformService.isIOS();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterContentInit(): void {
    this.initKeyManager();

    this.selectionModel.changed
      .pipe(takeUntil(this.destroy))
      .subscribe((event: SelectionChange<SelectableItem<T>>): void => {
        event.added.forEach((item: SelectableItem<T>): void => item.select(false));
        event.removed.forEach((item: SelectableItem<T>): void => item.deselect(false));
        this.selectedItems.set(this.selectionModel.selected);
      });

    this.optionSelectionChanges.pipe(takeUntil(this.destroy)).subscribe((event: SelectionChangeEvent<SelectableItem<T>>): void => {
      this.onSelect(event.source, event.fromView);
      if (event.fromView && this.isOpen()) {
        this.close();
        this.focus();
      }
    });

    this.initAriaAttributes();
  }

  readonly ariaActiveDescendantAttr: Signal<string | null> = computed(() => {
    if (this.panelDisplayed() && this.keyManager !== undefined && this.keyManager.activeItem !== null) {
      return this.keyManager.activeItem.id;
    }
    return null;
  });

  initAriaAttributes(): void {
    const ariaLabel = this.ariaLabel() ?? this.placeholder();
    if (ariaLabel !== undefined && ariaLabel !== null) {
      this.labelId.set(this.idService.generateNamespaceSequentialId(DropdownComponent.LABEL_ID_PREFIX));
      this.generateLabel.set(true);
    }
  }

  toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  onAttached(): void {
    const overlay = this.connectedOverlay();
    if (overlay?.overlayRef) {
      overlay.overlayRef.updateSize({ maxHeight: this.panelMaxHeight() });

      afterNextRender(() => {
        overlay.overlayRef.updatePosition();
        this.syncPanelPositionWithDom();
      }, { injector: this.injector });

      overlay.overlayRef.detachments()
        .pipe(take(1), takeUntil(this.destroy))
        .subscribe(() => this.close());
    }
  }

  close(): void {
    if (this.panelDisplayed()) {
      this.panelDisplayed.set(false);
      this.keyManager.withHorizontalOrientation('ltr');
    }
  }

  isOpen(): boolean {
    return this.panelDisplayed();
  }

  isEmpty(): boolean {
    return this.selectedItems().length === 0;
  }

  open(): void {
    if (this.isDisabled() || this.items().length === 0 || this.panelDisplayed()) {
      return;
    }

    const toggleRect = this.elementRef.nativeElement.getBoundingClientRect();
    this.toggleRect.set(toggleRect);

    const viewportRect = this.viewportRuler.getViewportRect();
    const maxHeight = this.calculatePanelMaxHeight(viewportRect.height);
    this.panelMaxHeight.set(maxHeight);

    this.updatePreferredPositions(toggleRect, viewportRect.height, maxHeight);

    this.panelDisplayed.set(true);
    this.keyManager.withHorizontalOrientation(null);
    this.highlightItem();

    this.handleAfterOpenActions();
  }

  private calculatePanelMaxHeight(viewportHeight: number): number {
    const userMaxHeight = this.userDefinedPanelMaxHeight();
    const defaultMaxHeight = viewportHeight * 0.4;
    return (userMaxHeight !== undefined && userMaxHeight < defaultMaxHeight) ? userMaxHeight : defaultMaxHeight;
  }

  private updatePreferredPositions(toggleRect: DOMRect, viewportHeight: number, maxHeight: number): void {
    const spaceBelow = viewportHeight - toggleRect.bottom;
    const spaceAbove = toggleRect.top;

    if (spaceBelow >= maxHeight || spaceBelow >= spaceAbove) {
      this.preferredPositions.set([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' }
      ]);
      this.panelPosition.set('bottom');
    } else {
      this.preferredPositions.set([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
      ]);
      this.panelPosition.set('top');
    }
  }

  private handleAfterOpenActions(): void {
    afterNextRender(() => {
      if (this.panel()) {
        this.scrollActiveItemIntoView();
        if (this.platformIOS && this.items().length > 0) {
          this.items()[0].focus();
        }
      }
    }, { injector: this.injector });
  }

  private syncPanelPositionWithDom(): void {
    const panelEl = this.panel()?.nativeElement;
    const toggleEl = this.elementRef.nativeElement;
    if (panelEl && toggleEl) {
      const panelRect = panelEl.getBoundingClientRect();
      const toggleRect = toggleEl.getBoundingClientRect();
      const actualPos = panelRect.top < toggleRect.top ? 'top' : 'bottom';
      if (this.panelPosition() !== actualPos) {
        this.panelPosition.set(actualPos);
      }
    }
  }

  isToggleHidden(): boolean {
    return this.toggleType() === 'none';
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  writeValue(value: T | null | undefined): void {
    this.value.set(value);
    if (this.items().length > 0) {
      this.setSelectionByValue(value);
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    this.panelPosition.set(event.connectionPair.originY);
  }

  getSelectedItem(): SelectableItem<T> {
    return this.selectedItems()[0];
  }

  hasSelection(): boolean {
    return this.selectedItems().length > 0;
  }

  private onKeyDownOpen(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP;
    const keyManager = this.keyManager;
    let preventDefault = true;

    if (keyCode === KeyCode.HOME) {
      keyManager.setFirstItemActive();
    } else if (keyCode === KeyCode.END) {
      keyManager.setLastItemActive();
    } else if (isArrowKey && event.altKey) {
      this.close();
    } else if ((keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE) && keyManager.activeItem !== null) {
      keyManager.activeItem.select(true);
    } else {
      keyManager.onKeydown(event);
      preventDefault = false;
    }
    if (preventDefault) {
      event.preventDefault();
    }
  }

  private onKeyDownClose(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP ||
      keyCode === KeyCode.ARROW_LEFT || keyCode === KeyCode.ARROW_RIGHT;
    const isOpenKey = keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE;

    if (isOpenKey || isArrowKey) {
      event.preventDefault();
      this.open();
    } else {
      this.keyManager.onKeydown(event);
    }
  }

  private highlightItem(): void {
    if (this.isEmpty()) {
      this.keyManager.setFirstItemActive();
    } else {
      this.keyManager.setActiveItem(this.selectionModel.selected[0]);
    }
  }

  private initKeyManager(): void {
    this.createKeyManager();

    this.keyManager.tabOut.pipe(takeUntil(this.destroy)).subscribe((): void => {
      this.focus();
      this.close();
    });

    this.keyManager.change.pipe(takeUntil(this.destroy)).subscribe((): void => {
      if (this.panelDisplayed() && this.panel() !== undefined) {
        this.scrollActiveItemIntoView();
      } else if (!this.panelDisplayed() && this.keyManager.activeItem !== null &&
        this.keyManager.activeItem !== undefined) {
        this.keyManager.activeItem.select(true);
      }
    });
    this.keyManagerInitialized = true;
  }

  private reinitializeKeyManager(): void {
    // Recreate the key manager with updated items when they change dynamically
    // (e.g., asynchronously loaded dropdown items). No need to re-subscribe to
    // tabOut and change events as they are already scoped to takeUntil(this.destroy).
    this.createKeyManager();
  }

  private createKeyManager(): void {
    if (this.platformIOS) {
      this.keyManager = new FocusKeyManager<DropdownItemComponent<T>>(this.items())
        .withTypeAhead()
        .withVerticalOrientation()
        .withHorizontalOrientation('ltr');
    } else {
      this.keyManager = new ActiveDescendantKeyManager<DropdownItemComponent<T>>(this.items())
        .withTypeAhead()
        .withVerticalOrientation()
        .withHorizontalOrientation('ltr');
    }
  }

  private getItemHeight(): number {
    const items = this.items();
    if (items.length > 0) {
      const firstItem = items[0];
      const height = getComputedStyle(firstItem.elementRef.nativeElement).height;
      return height !== undefined ? Math.round(parseFloat(height)) : 0;
    }
    return 0;
  }

  private scrollActiveItemIntoView(): void {
    const activeOptionIndex = this.keyManager.activeItemIndex ?? 0;

    this.itemHeight = this.getItemHeight();
    const panelHeight = this.panelMaxHeight();
    const displayNumber = Math.floor(panelHeight / this.itemHeight);
    this.panelMaxHeight.set(this.itemHeight * (displayNumber + 0.55));

    const panelEl = this.panel();
    if (panelEl) {
      panelEl.nativeElement.scrollTop = this.getOptionScrollPosition(
        activeOptionIndex,
        this.itemHeight,
        panelEl.nativeElement.scrollTop,
        this.panelMaxHeight()
      );
    }
  }

  private onSelect(item: SelectableItem<T>, isUserInput: boolean): void {
    const wasSelected = this.selectionModel.isSelected(item);

    if (item.getValue() === null || item.getValue() === undefined) {
      item.deselect();
      this.selectionModel.clear();
      this.propagateChanges(item.getValue(), isUserInput);
    } else {
      if (item.isSelected()) {
        this.selectionModel.select(item);
      } else {
        this.selectionModel.deselect(item);
      }
      if (isUserInput) {
        this.keyManager.setActiveItem(item);
      }
    }
    if (wasSelected !== this.selectionModel.isSelected(item)) {
      this.propagateChanges(undefined, isUserInput);
    }
    if (isUserInput) {
      this.onTouchedCallback();
      this.touch.emit();
    }
  }

  private propagateChanges(value: T | null | undefined = undefined, isUserInput: boolean = false): void {
    const selected = this.getSelectedItem();
    const updatedValue = selected === undefined ? value : selected.getValue();
    this.value.set(updatedValue);
    if (isUserInput) {
      this.onChangeCallback(updatedValue);
    }
  }

  private setSelectionByValue(value: T | null | undefined): void {
    this.selectionModel.clear();
    const item: SelectableItem<T> | undefined = this.selectValue(value);
    if (item !== undefined && this.keyManager !== undefined) {
      this.keyManager.setActiveItem(item);
    }
  }

  private selectValue(value: T | null | undefined): DropdownItemComponent<T> | undefined {
    const dropdownItem = this.items()
      .find((item: DropdownItemComponent<T>): boolean => {
        const itemValue = item.value();
        return itemValue != null && value != null && this.comparator()(itemValue, value);
      });

    if (dropdownItem !== undefined) {
      this.selectionModel.select(dropdownItem);
    }

    return dropdownItem;
  }

  private getOptionScrollPosition(optionIndex: number, optionHeight: number,
                                  currentScrollPosition: number, panelHeight: number): number {
    const optionOffset = optionIndex * optionHeight;

    if (optionOffset < currentScrollPosition) {
      return optionOffset;
    }

    if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
      return Math.max(0, optionOffset - panelHeight + optionHeight);
    }

    return currentScrollPosition;
  }
}
